import { CalendarEventTypes, type ProgramCalendarEventType } from "@/modules/calendar/calendarTypes"
import { UserEntity } from "@/modules/profile/userProfile/userEntity"
import type { ProgramType, ProgramVisibility } from "@/modules/programs/types/programTypes"
import { SessionBlockEntityFactory, type SessionBlockEntityType } from "@/modules/sessions/entities/sessionBlockEntity"
import { SessionBlockExerciseEntity } from "@/modules/sessions/entities/sessionBlockExerciseEntity"
import type { UpdateSessionInputDto, WebSessionDetailsDto } from "@/modules/sessions/gateway/programInfiniteSessionDto"
import type { TemplateElementDto } from "@/modules/template/gateway/templateDto"
import { TemplateType } from "@/modules/template/types/templateType"

export class TemplateSessionEntity {
  id: string
  name: string
  description: string | null
  date: string | null
  owner: UserEntity
  programId: string | null
  programType: ProgramType | null
  programVisibility: ProgramVisibility | null
  programName: string | null
  hideSessionContent: boolean
  cardioTag: boolean
  gymnasticTag: boolean
  weightliftingTag: boolean
  index: number | null
  virtualProgramByDateIndex: number | null
  blocks: SessionBlockEntityType[]
  sessionCompletedByUser: boolean

  private constructor(session: TemplateSessionEntity) {
    this.id = session.id
    this.name = session.name
    this.description = session.description
    this.date = session.date
    this.owner = session.owner
    this.programId = session.programId
    this.programType = session.programType
    this.programVisibility = session.programVisibility
    this.programName = session.programName
    this.hideSessionContent = session.hideSessionContent
    this.cardioTag = session.cardioTag
    this.gymnasticTag = session.gymnasticTag
    this.weightliftingTag = session.weightliftingTag
    this.index = session.index
    this.virtualProgramByDateIndex = session.virtualProgramByDateIndex
    this.blocks = session.blocks
    this.sessionCompletedByUser = session.sessionCompletedByUser
  }

  static fromDto(dto: WebSessionDetailsDto): TemplateSessionEntity {
    return new TemplateSessionEntity({
      id: dto.id,
      name: dto.name,
      description: dto.description,
      date: dto.date,
      owner: UserEntity.fromDto(dto.owner),
      programId: dto.programId,
      programType: dto.programType,
      programVisibility: dto.programVisibility,
      programName: dto.programName,
      hideSessionContent: dto.hideSessionContent ?? false,
      cardioTag: dto.cardioTag,
      gymnasticTag: dto.gymnasticTag,
      weightliftingTag: dto.weightliftingTag,
      index: dto.index,
      virtualProgramByDateIndex: dto.virtualProgramByDateIndex,
      blocks: dto.blocks.map((block) => SessionBlockEntityFactory.fromDto(block)),
      sessionCompletedByUser: dto.sessionCompletedByUser,
    })
  }

  static toCalendarEvent(session: TemplateSessionEntity): ProgramCalendarEventType {
    return {
      _type: CalendarEventTypes.UNDATED_EVENT,
      id: session.id,
      title: session.name,
      editable: true,
      dayIndex: session.index ?? 0,
      restDay: false,
      blocks: session.blocks,
      cardioTag: session.cardioTag,
      gymnasticTag: session.gymnasticTag,
      weightliftingTag: session.weightliftingTag,
      hideSessionContent: session.hideSessionContent,
    }
  }

  static fromEntityToInputDto(session: TemplateSessionEntity): UpdateSessionInputDto {
    return {
      name: session.name,
      description: session.description,
      date: session.date,
      duration: 0,
      blocks: session.blocks.map((block) => SessionBlockEntityFactory.fromEntityToInputDto(block)),
      hideSessionContent: session.hideSessionContent,
      weightliftingTag: session.weightliftingTag,
      gymnasticTag: session.gymnasticTag,
      cardioTag: session.cardioTag,
      restDay: false,
      index: session.index,
      virtualProgramByDateIndex: session.virtualProgramByDateIndex,
    }
  }
}

export type TemplateSessionElementEntity = {
  type: typeof TemplateType.SESSION
  session: TemplateSessionEntity
}

export type TemplateWeekElementEntity = {
  type: typeof TemplateType.WEEK
  sessions: TemplateSessionEntity[]
}

export type TemplateExerciseElementEntity = {
  type: typeof TemplateType.EXERCISE
  exercise: SessionBlockExerciseEntity
}

export type TemplateElementEntity =
  | TemplateSessionElementEntity
  | TemplateWeekElementEntity
  | TemplateExerciseElementEntity

export const TemplateElementEntityFactory = {
  fromDto: (dto: TemplateElementDto): TemplateElementEntity => {
    switch (dto.type) {
      case TemplateType.SESSION:
        return {
          type: TemplateType.SESSION,
          session: TemplateSessionEntity.fromDto(dto.session),
        }
      case TemplateType.WEEK:
        return {
          type: TemplateType.WEEK,
          sessions: dto.sessions.map((session) => TemplateSessionEntity.fromDto(session)),
        }
      case TemplateType.EXERCISE:
        return {
          type: TemplateType.EXERCISE,
          exercise: SessionBlockExerciseEntity.fromDto(dto.exercise),
        }
      default: {
        const exhaustiveCheck: never = dto
        throw new Error(`Invalid template element ${String(exhaustiveCheck)}`)
      }
    }
  },
}
